// Store — estado em memória do painel + carga inicial + realtime.
//
// Responsabilidades:
//   * Guardar as 7 coleções já mapeadas para objetos (ofertas com criativos
//     aninhados; demStatus como mapa "conta|offer" → objeto).
//   * load(): carrega tudo do Supabase pós-login e monta a árvore.
//   * subscribeRealtime(): escuta postgres_changes de TODAS as tabelas e
//     atualiza o estado local, notificando a UI para re-render da área afetada.
//
// A persistência (insert/update/delete) é feita diretamente pelas telas via
// repositories; o store concentra o estado e a sincronização realtime.
import { supabase } from './lib/supabase.js'
import { wasRecent } from './lib/recent-writes.js'
import {
  ofertasRepo,
  criativosRepo,
  criativoParaObjeto,
  contasRepo,
  perfisRepo,
  proxysRepo,
  diariasRepo,
  demandasRepo
} from './data/repositories.js'

// Estado central. As telas leem daqui; as mutações passam pelos repositories.
export const state = {
  ofertas: [],
  contas: [],
  perfis: [],
  proxys: [],
  diarias: [],
  demStatus: {} // chave "conta|offer" → { id, conta, offer, status }
}

// Assinantes interessados em mudanças vindas do realtime (área → callback).
// main.js registra um callback que re-renderiza somente a área visível.
const listeners = new Set()
export function onRealtime(cb) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
function notify(area) {
  listeners.forEach(cb => cb(area))
}

/* ============ Carga inicial ============ */
export async function load() {
  const [ofertas, criativos, contas, perfis, proxys, diarias, demandas] = await Promise.all([
    ofertasRepo.list(),
    criativosRepo.list(),
    contasRepo.list(),
    perfisRepo.list(),
    proxysRepo.list(),
    diariasRepo.list(),
    demandasRepo.list()
  ])

  // Monta a árvore oferta → criativos (já ordenados por posicao/created_at).
  const porOferta = new Map()
  ofertas.forEach(o => {
    o.criativos = []
    porOferta.set(o.id, o)
  })
  criativos.forEach(c => {
    const of = porOferta.get(c.oferta_id)
    if (of) of.criativos.push(c)
  })

  state.ofertas = ofertas
  state.contas = contas
  state.perfis = perfis
  state.proxys = proxys
  state.diarias = diarias

  // demStatus como mapa por chave lógica conta|offer.
  state.demStatus = {}
  demandas.forEach(d => {
    state.demStatus[d.conta + '|' + d.offer] = d
  })
}

/* ============ Próxima posição (mantém ordem coerente) ============ */
// Itens novos entram no topo (unshift no app legado) → posicao menor que todas.
export function posicaoTopo(lista) {
  if (!lista.length) return 0
  return Math.min(...lista.map(x => x.posicao ?? 0)) - 1
}
// Itens de criativo entram no fim da oferta (push no app legado).
export function posicaoFim(lista) {
  if (!lista.length) return 0
  return Math.max(...lista.map(x => x.posicao ?? 0)) + 1
}

/* ============ Realtime ============ */
// Guarda o canal para permitir unsubscribe no logout.
let canal = null

// Aplica um evento de OUTRO cliente ao estado local e dispara re-render.
function aplicarEvento(tabela, eventType, novo, antigo) {
  // Eco do PRÓPRIO cliente: o estado já foi atualizado de forma otimista pela
  // tela. Ignora para não re-renderizar e tirar o foco do campo em edição.
  const rid = (novo && novo.id) || (antigo && antigo.id)
  if (wasRecent(rid)) return

  switch (tabela) {
    case 'ofertas':
      aplicarColecao(state.ofertas, eventType, novo, antigo, r => {
        const o = ofertasRepo.paraObjeto(r)
        o.criativos = [] // será preenchido abaixo, na fusão
        return o
      }, 'ofertas', true)
      break
    case 'criativos':
      aplicarCriativo(eventType, novo, antigo)
      break
    case 'contas':
      aplicarColecao(state.contas, eventType, novo, antigo, contasRepo.paraObjeto, 'contas')
      break
    case 'perfis':
      aplicarColecao(state.perfis, eventType, novo, antigo, perfisRepo.paraObjeto, 'perfis')
      break
    case 'proxys':
      aplicarColecao(state.proxys, eventType, novo, antigo, proxysRepo.paraObjeto, 'proxys')
      break
    case 'diarias':
      aplicarColecao(state.diarias, eventType, novo, antigo, diariasRepo.paraObjeto, 'gestores')
      break
    case 'demandas_status':
      aplicarDemanda(eventType, novo, antigo)
      break
  }
}

// Aplica INSERT/UPDATE/DELETE em uma lista simples, preservando a posição.
// `preservaCriativos` evita zerar os criativos já carregados ao atualizar oferta.
function aplicarColecao(lista, eventType, novo, antigo, paraObjeto, area, preservaCriativos) {
  if (eventType === 'INSERT') {
    if (lista.some(x => x.id === novo.id)) return // já existe (evita duplicar)
    lista.push(paraObjeto(novo))
  } else if (eventType === 'UPDATE') {
    const i = lista.findIndex(x => x.id === novo.id)
    if (i === -1) {
      lista.push(paraObjeto(novo))
    } else {
      const filhos = preservaCriativos ? lista[i].criativos : null
      lista[i] = paraObjeto(novo)
      if (filhos) lista[i].criativos = filhos
    }
  } else if (eventType === 'DELETE') {
    const i = lista.findIndex(x => x.id === antigo.id)
    if (i !== -1) lista.splice(i, 1)
  }
  ordenarPorPosicao(lista)
  notify(area)
}

// Criativos vivem dentro da oferta correspondente.
function aplicarCriativo(eventType, novo, antigo) {
  const ref = novo || antigo
  if (!ref) return
  if (eventType === 'DELETE') {
    // Sem oferta_id no payload de delete em alguns casos → varre todas.
    state.ofertas.forEach(of => {
      const i = of.criativos.findIndex(c => c.id === antigo.id)
      if (i !== -1) of.criativos.splice(i, 1)
    })
    notify('ofertas')
    return
  }
  const of = state.ofertas.find(o => o.id === ref.oferta_id)
  if (!of) return
  const obj = criativoParaObjeto(novo)
  const i = of.criativos.findIndex(c => c.id === obj.id)
  if (eventType === 'INSERT') {
    if (i === -1) of.criativos.push(obj)
  } else if (eventType === 'UPDATE') {
    if (i === -1) of.criativos.push(obj)
    else of.criativos[i] = obj
  }
  ordenarPorPosicao(of.criativos)
  notify('ofertas')
}

// demandas_status → atualiza o mapa por chave conta|offer.
function aplicarDemanda(eventType, novo, antigo) {
  if (eventType === 'DELETE') {
    const chave = (antigo.conta || '') + '|' + (antigo.offer || '')
    delete state.demStatus[chave]
  } else {
    const obj = demandasRepo.paraObjeto(novo)
    state.demStatus[obj.conta + '|' + obj.offer] = obj
  }
  notify('gestores')
}

// Ordena uma lista por posicao (estável o suficiente para a UI).
function ordenarPorPosicao(lista) {
  lista.sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0))
}

// Assina mudanças de todas as tabelas. Eventos do PRÓPRIO cliente também
// chegam aqui; as funções acima são idempotentes (checam id), então não
// duplicam o que a tela já aplicou localmente.
export function subscribeRealtime() {
  if (canal) return
  canal = supabase.channel('painel-trafego')
  const tabelas = ['ofertas', 'criativos', 'contas', 'perfis', 'proxys', 'diarias', 'demandas_status']
  tabelas.forEach(tabela => {
    canal.on('postgres_changes', { event: '*', schema: 'public', table: tabela }, payload => {
      aplicarEvento(tabela, payload.eventType, payload.new, payload.old)
    })
  })
  canal.subscribe()
}

// Encerra a assinatura realtime (usado no logout).
export function unsubscribeRealtime() {
  if (canal) {
    supabase.removeChannel(canal)
    canal = null
  }
}

// Limpa o estado em memória (logout).
export function clear() {
  state.ofertas = []
  state.contas = []
  state.perfis = []
  state.proxys = []
  state.diarias = []
  state.demStatus = {}
}
